import { useEffect, useMemo, useState } from "react";
import { useTranslation, untranslated, type TranslationKey } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { getExternalLinkStats, type ExternalLinkListItem } from "./externalLinks.api";
import { LINK_DIALOG_WIDTH, formatGridTimestamp } from "./externalLinks.helpers";
import type { GetExternalLinkStatsResponse } from "@ServerTypes/externalLinks/externalLinks.schema";

/**
 * "View Stats" — the link's full access timeline.
 *
 * A real event list rather than counters: every view and download with its timestamp and IP where
 * available, interleaved with the creator-side edits and password changes, so an owner can answer
 * "who opened this, and has anything about it changed" from one screen.
 */
type ExternalLinkStatsModalProps = {
  link: ExternalLinkListItem;
  onClose: () => void;
};

type StatsEvent = NonNullable<GetExternalLinkStatsResponse["events"]>[number];

/**
 * Event type → icon + label.
 *
 * Keyed by the SERVER'S event union, not by `string`: adding a type to
 * `EXTERNAL_LINK_EVENT_VALUES` then becomes a compile error here rather than a row that renders its
 * raw enum value. (Keyed by `string` this map silently accepted an incomplete set, which is exactly
 * how `reinstated` would have shipped unlabelled.)
 *
 * `labelKey` is typed `TranslationKey` so a label missing from en.json is also a compile error.
 */
const EVENT_PRESENTATION: Record<StatsEvent["type"], { icon: string; labelKey: TranslationKey }> = {
  viewed: { icon: "EyeOpen", labelKey: "Viewed" },
  downloaded: { icon: "download", labelKey: "Downloaded" },
  link_edited: { icon: "editProfile", labelKey: "Link edited" },
  password_changed: { icon: "Lock", labelKey: "Password changed" },
  created: { icon: "Link", labelKey: "Link created" },
  revoked: { icon: "delete", labelKey: "Link revoked" },
  reinstated: { icon: "refresh", labelKey: "Link enabled" },
  disabled_by_admin: { icon: "Lock", labelKey: "Disabled by admin" },
};

function ExternalLinkStatsModal({ link, onClose }: Readonly<ExternalLinkStatsModalProps>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<StatsEvent[]>([]);
  const [viewCount, setViewCount] = useState(0);
  const [downloadCount, setDownloadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "access">("all");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const res = await getExternalLinkStats({
        linkId: link._id,
        // Filtering server-side rather than client-side so the "Access only" view stays correct
        // once the log outgrows one page.
        types: filter === "access" ? ["viewed", "downloaded"] : undefined,
        take: 200,
      });
      if (cancelled) return;
      if (res.status) {
        setEvents(res.events ?? []);
        setViewCount(res.viewCount ?? 0);
        setDownloadCount(res.downloadCount ?? 0);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [link._id, filter]);

  const summary = useMemo(
    () => [
      { label: t("Views"), value: String(viewCount) },
      { label: t("Downloads"), value: String(downloadCount) },
      { label: t("Events"), value: String(events.length) },
    ],
    [viewCount, downloadCount, events.length, t],
  );

  const filterTab = (key: "all" | "access", label: string) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${
        filter === key
          ? "border-system_colour_1 bg-selection_primary"
          : "border-stroke_primary bg-white hover:bg-card_hover_color"
      }`}
    >
      {label}
    </button>
  );

  /**
   * Filters the LOADED events by event label or IP (PRD 3.7's grid-header search).
   *
   * Client-side deliberately, unlike the portal register's server-side search: this list is one link's
   * timeline, already bounded by the request's `take`, so filtering it in place is the whole set — not a
   * misleading subset. Matching on the TRANSLATED label rather than the raw enum is what makes typing
   * "download" work for a user who never sees `downloaded`.
   */
  const trimmedSearch = search.trim().toLowerCase();
  const visibleEvents = useMemo(() => {
    if (!trimmedSearch) return events;
    return events.filter((event) => {
      const presentation = EVENT_PRESENTATION[event.type];
      const label = (presentation ? t(presentation.labelKey) : event.type).toLowerCase();
      return label.includes(trimmedSearch) || (event.ip ?? "").toLowerCase().includes(trimmedSearch);
    });
  }, [events, trimmedSearch, t]);

  return (
    <ModalStateDialogComponent
      isVisible
      width={LINK_DIALOG_WIDTH}
      headerTitle={
        <div className="flex min-w-0 items-center gap-2">
          <Text className="truncate">{t("Link stats")}</Text>
          <Text className="truncate text-sm font-normal text-text_light">{untranslated(`— ${link.name}`)}</Text>
        </div>
      }
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            {summary.map((card) => (
              <div key={card.label} className="rounded-md border border-stroke_primary px-3 py-2">
                <Text className="block text-xs text-text_light">{card.label}</Text>
                <Text className="mt-1 block text-lg font-semibold">{untranslated(card.value)}</Text>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {filterTab("all", t("All events"))}
            {filterTab("access", t("Views and downloads"))}
            <div className="flex-1" />
            {/* Collapsed to an icon that expands on click (PRD 6), at the right of the header strip. */}
            {searchOpen ? (
              <div className="flex h-8 w-56 items-center gap-2 rounded-md border border-stroke_primary px-2">
                <IconComponent name="search-icon" width="0.875rem" height="0.875rem" />
                <input
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("Search")}
                  aria-label={t("Search events")}
                  className="min-w-0 flex-1 border-0 bg-transparent text-xs outline-none"
                />
                <button
                  type="button"
                  aria-label={t("Close search")}
                  onClick={() => {
                    setSearchOpen(false);
                    setSearch("");
                  }}
                >
                  <IconComponent name="close" width="0.75rem" height="0.75rem" />
                </button>
              </div>
            ) : (
              <button type="button" aria-label={t("Search")} onClick={() => setSearchOpen(true)} className="px-1">
                <IconComponent name="search-icon" width="1rem" height="1rem" />
              </button>
            )}
          </div>

          <div className="overflow-hidden rounded-md border border-stroke_primary">
            <div className="grid grid-cols-[minmax(0,1fr)_13rem_10rem] items-center border-b border-stroke_primary bg-light_background px-3 py-2 text-xs font-semibold text-text_light">
              <span>{t("Event Type")}</span>
              <span>{t("Date & Time")}</span>
              <span>{t("IP Address")}</span>
            </div>
            <div className="max-h-80 overflow-auto">
              {loading && <div className="px-3 py-8 text-center text-sm text-text_light">{t("Loading...")}</div>}
              {!loading && !visibleEvents.length && (
                <div className="px-3 py-8 text-center text-sm text-text_light">
                  {trimmedSearch ? t("No events match your search.") : t("No activity yet")}
                </div>
              )}
              {!loading &&
                visibleEvents.map((event) => {
                  const presentation = EVENT_PRESENTATION[event.type];
                  return (
                    <div
                      key={event._id}
                      className="grid grid-cols-[minmax(0,1fr)_13rem_10rem] items-center border-b border-stroke_primary px-3 py-2 text-sm last:border-b-0"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <IconComponent name={presentation?.icon ?? "info"} width="1rem" height="1rem" />
                        <span className="truncate">
                          {/* Server event types are a closed set; anything unmapped is shown raw
                              rather than blank so a new type is visible instead of silently missing. */}
                          {presentation ? t(presentation.labelKey) : untranslated(event.type)}
                        </span>
                        {!!event.actorName && (
                          <span className="truncate text-xs text-text_light">
                            {untranslated(`· ${event.actorName}`)}
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-xs text-text_light">
                        {untranslated(formatGridTimestamp(event.at))}
                      </span>
                      {/* "—" rather than blank: FR-27 records IP "where available", and creator-side
                          events genuinely have none. */}
                      <span className="font-mono text-xs text-text_light">{untranslated(event.ip ?? "—")}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      }
    />
  );
}

export default ExternalLinkStatsModal;
